import { delay, all, call, put, race, select, take, takeLatest } from 'redux-saga/effects'
import _ from 'lodash'
import ls from 'local-storage'
import moment from 'moment'

import config from '../../../../../../../../config'

import { error, warning } from '../../../../../../../../services/toastr'

import { entitySearchRequest, entityStackSearchSetEntities, entitySearchEnd } from './actions'

import { sessionIdSelector, userEmailSelector } from './selectors'

import { parseSearch, getStoredData } from './helpers'

import {
    ENTITY_SEARCH_REQUEST_FAILED,
    ENTITY_SEARCH_REQUEST_SUCCESS,
    ENTITY_SEARCH_START,
    LOCALE_PREFIX,
} from './constants'

export function* storeSearchResult(storageData) {
    const { searchIndex, searchValue, searchResults } = storageData
    let storageSearchCollection = yield call(ls.get, searchIndex)
    const {
        components: {
            entityStack: {
                search: { historySize },
            },
        },
    } = config

    if (storageSearchCollection) {
        // filter found collection from storage
        storageSearchCollection = storageSearchCollection.filter(
            (storageElement) => storageElement.searchValue !== searchValue
        )
    }

    // create new storage fata
    const searchDate = moment()
    const nextStorageData = { searchValue, searchResults, searchDate }
    storageSearchCollection = storageSearchCollection
        ? [nextStorageData, ...storageSearchCollection].slice(0, historySize - 1)
        : [nextStorageData]

    yield call(ls.set, searchIndex, storageSearchCollection)

    return null
}

export function* entitySearchCallSaga(_action) {
    const { searchValue: nextSearchValue } = _.get(_action, 'data', {})

    // Stop search without value
    if (!nextSearchValue) {
        yield put(entitySearchEnd(''))

        return null
    }

    const sessionId = yield select(sessionIdSelector)
    const userEmail = yield select(userEmailSelector)
    const {
        components: {
            entityStack: {
                search: { quickAutocomplete, invalidationTime, simulatedPreloadDelay },
            },
        },
    } = config

    // Storage collection index based on user-email
    const searchIndex = `search-collection-${userEmail}`

    // first empty current search results
    yield put(entityStackSearchSetEntities([]))

    // config autocomplete setup for quick initialize list from local storage
    if (quickAutocomplete) {
        const storageSearchCollection = yield call(ls.get, searchIndex)
        if (storageSearchCollection && getStoredData(storageSearchCollection, nextSearchValue)) {
            const { searchResults: storedSearchResults, searchDate } = getStoredData(
                storageSearchCollection,
                nextSearchValue
            )
            const currentDate = moment()

            if (currentDate.diff(searchDate, 'hours') < invalidationTime) {
                // wait 70 ms for "request" efect
                yield delay(simulatedPreloadDelay)
                yield put(entityStackSearchSetEntities(storedSearchResults))
                // just merge same search results after payload success
                // stop search preload
                yield put(entitySearchEnd(nextSearchValue))
            }
        }
    }

    // run real search request for fresh data
    const searchResult = yield all({
        response: race({
            success: take(ENTITY_SEARCH_REQUEST_SUCCESS),
            failed: take(ENTITY_SEARCH_REQUEST_FAILED),
        }),
        request: put(entitySearchRequest(parseSearch(nextSearchValue), sessionId)),
    })

    if (searchResult.response.success) {
        // store last success response data
        const searchResults = _.get(searchResult, 'response.success.payload.response.body', [])

        if (searchResults.length) {
            const storageData = { searchIndex, searchValue: nextSearchValue, searchResults }

            // update storage with fresh backend response data
            yield call(storeSearchResult, storageData)
            yield put(entityStackSearchSetEntities(searchResults))
        } else {
            warning({ id: `${LOCALE_PREFIX}.searchResponse.warning` })
        }
    } else if (searchResult.response.failed) {
        error({ id: `${LOCALE_PREFIX}.searchResponse.error` })
    }

    // finaly stop search preload
    yield put(entitySearchEnd(nextSearchValue))

    return null
}

// Entity stack search saga
export default function* entitySearchSaga() {
    yield all([takeLatest(ENTITY_SEARCH_START, entitySearchCallSaga)])
}
